import { Box, Button, Skeleton, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import config from "../../config/config";
import { CONFIRMATION_DELAY } from "../../constants/const";
import { Messages } from "../../constants/messages";
import { getGameBankAllowance } from "../../helper/contractFunctions/MafiaGameBank";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";
import {
  approveGameCashAction,
  buyNarcsAction,
  getCityMarketPriceAction,
  getNarcsHoldingsAction,
  sellNarcsAction,
} from "../../reducers/game.slice";
import { updateCashBalance } from "../../reducers/profile.slice";
import { SmugglingGood } from "../../types/Smuggling/SmugglingGood";
import { toastInfo, toUSDFormat } from "../../utils/utils";

import useStyles from "./index.styles";
import { debounce } from "lodash";

const NarcoticsList = ({
  setSmugglingBoozeType,
  setSmugglingBoozeCount,
  narcsCountLimit,
  narcsPrices,
  narcsHoldings,
  isLoadingNarcsPrices,
  disabled,
}: {
  setSmugglingBoozeType: (type: string) => void;
  setSmugglingBoozeCount: (count: number) => void;
  narcsCountLimit: number;
  narcsPrices: number[];
  narcsHoldings: SmugglingGood[];
  isLoadingNarcsPrices: boolean;
  disabled: boolean;
}) => {
  const { classes } = useStyles();
  const { account } = useWallet();
  const dispatch = useAppDispatch();
  const { myProfile } = useAppSelector((state) => state.profile);
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { balance, updateBalance } = useGameBankBalance(
    account,
    signMsg,
    signature
  );
  const { businessProfile } = useAppSelector((state) => state.game);

  const products = useMemo(() => {
    return [
      { name: "Glue", price: narcsPrices[0] || 0 },
      { name: "Marijuana", price: narcsPrices[1] || 0 },
      { name: "Amphetamine", price: narcsPrices[2] || 0 },
      { name: "Cocaine", price: narcsPrices[3] || 0 },
      { name: "Morphine", price: narcsPrices[4] || 0 },
      { name: "Opium", price: narcsPrices[5] || 0 },
      { name: "Heroin", price: narcsPrices[6] || 0 },
    ];
  }, [narcsPrices]);

  const [amounts, setAmounts] = useState(products.map(() => 0));
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalHoldingCount, setTotalHoldingCount] = useState(0);

  const narcsHoldingCounts = useMemo(() => {
    return [...new Array(7)].map(
      (_, index) => narcsHoldings.filter((item) => item.typeId === index).length
    );
  }, [narcsHoldings]);

  // Create debounced dispatch function
  const debouncedGetPrice = useMemo(
    () =>
      debounce((city: number) => {
        dispatch(getCityMarketPriceAction(city));
      }, 500), // 500ms delay
    [dispatch]
  );
  const handleAmountChange = (index: number, value: string) => {
    const newAmounts = [...amounts];
    newAmounts[index] = Math.max(0, parseInt(value) || 0);
    setAmounts(newAmounts);

    debouncedGetPrice(myProfile.city ?? 0);
  };

  const handleBuy = async () => {
    if (!businessProfile?.address) {
      toastInfo("Narcotics is for sale on marketplace or OTC trade");
      return;
    }

    if (totalAmount === 0) {
      toastInfo(Messages.BOOZE.BUY.AMOUNT_CAN_NOT_ZERO);
      return;
    }
    dispatch(getCityMarketPriceAction(myProfile.city ?? 0));

    if (totalAmount + totalHoldingCount > narcsCountLimit) {
      toastInfo(Messages.NARCS.BUY.LIMIT_EXCEEDED);
      return;
    }

    if (totalPrice > balance) {
      toastInfo(Messages.NARCS.BUY.INSUFFICIENT_CASH);
      return;
    }

    const allowance = await getGameBankAllowance(
      account,
      config.smuggleMarketAddress
    );

    const itemTypes = amounts
      .map((amount, index) => [...new Array(amount)].map(() => index))
      .flat();

    const buyNarcs = async () => {
      dispatchTxAction(
        dispatch,
        buyNarcsAction({
          account,
          signMsg,
          signature,
          types: itemTypes,
        }),
        () => {
          setSmugglingBoozeType("Narcs");
          setSmugglingBoozeCount(totalAmount);
          updateBalance(account, signMsg, signature);
          dispatch(updateCashBalance(account));
          dispatch(getNarcsHoldingsAction(account));

          setAmounts([...new Array(narcsPrices.length)].map(() => 0));
        },
        CONFIRMATION_DELAY * 2
      );
    };

    if (allowance < totalPrice) {
      dispatchTxAction(
        dispatch,
        approveGameCashAction({
          account,
          spender: config.smuggleMarketAddress,
          cashAmount: 10 ** 18,
        }),
        () => {
          buyNarcs();
        },
        CONFIRMATION_DELAY
      );
    } else {
      buyNarcs();
    }
  };

  const handleSell = () => {
    if (!businessProfile?.address) {
      toastInfo("Narcotics is for sale on marketplace or OTC trade");
      return;
    }

    if (totalAmount === 0) {
      toastInfo(Messages.BOOZE.BUY.AMOUNT_CAN_NOT_ZERO);
      return;
    }
    dispatch(getCityMarketPriceAction(myProfile.city ?? 0));

    // Check limits using some() instead of forEach
    const hasExceededLimit = narcsHoldingCounts.some((count, index) => {
      return count < amounts[index];
    });

    if (hasExceededLimit) {
      toastInfo(Messages.BOOZE.SELL.LIMIT_EXCEEDED);
      return;
    }

    const itemIds = amounts
      .map((amount, index) => {
        return narcsHoldings
          .filter((item) => item.typeId === index)
          .map((item) => item.id)
          .slice(0, amount);
      })
      .flat();

    dispatchTxAction(
      dispatch,
      sellNarcsAction({
        account,
        signMsg,
        signature,
        itemIds,
      }),
      () => {
        setSmugglingBoozeType("Narcs");
        setSmugglingBoozeCount(totalAmount);
        updateBalance(account, signMsg, signature);
        dispatch(updateCashBalance(account));
        dispatch(getNarcsHoldingsAction(account));

        setAmounts([...new Array(narcsPrices.length)].map(() => 0));
      },
      CONFIRMATION_DELAY * 2
    );
  };

  const handleSelectAll = () => {
    setAmounts(narcsHoldingCounts);
    debouncedGetPrice(myProfile.city ?? 0);
  };

  useEffect(() => {
    let totalPrice = 0;
    amounts.forEach((amount, index) => {
      totalPrice += products[index].price * amount;
    });
    const totalAmount = amounts.reduce((acc, curr) => acc + curr, 0);

    setTotalAmount(totalAmount);
    setTotalPrice(totalPrice);
  }, [amounts, products]);

  useEffect(() => {
    const totalHoldingCount = narcsHoldingCounts.reduce(
      (acc, curr) => acc + curr,
      0
    );
    setTotalHoldingCount(totalHoldingCount);
  }, [narcsHoldingCounts]);

  return (
    <Box className={classes.tableContainer}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th className={classes.th}>Product</th>
            <th className={classes.th}>
              <Box>
                <Box
                  component={"img"}
                  src={"/assets/imgs/layout/currency.png"}
                ></Box>
                Price
              </Box>
            </th>
            <th className={classes.th}>Holdings</th>
            <th className={classes.th}>
              <Box>
                Amount
                <Button onClick={handleSelectAll}>All</Button>
              </Box>
            </th>
            <th className={classes.th}>
              <Box>
                <Box
                  component={"img"}
                  src={"/assets/imgs/layout/currency.png"}
                ></Box>
                Total
              </Box>
            </th>
          </tr>
        </thead>
        <tbody>
          {products.map((product, index) => {
            const total = product.price * amounts[index];
            return (
              <tr key={product.name}>
                <td className={classes.td}>
                  <Typography>{product.name}</Typography>
                </td>
                <td className={classes.td}>
                  {isLoadingNarcsPrices ? (
                    <Skeleton width={48} height={24} />
                  ) : (
                    <Typography>${product.price.toLocaleString()}</Typography>
                  )}
                </td>
                <td className={classes.td}>
                  <Typography>{narcsHoldingCounts[index]}</Typography>
                </td>
                <td className={classes.td}>
                  <input
                    type="number"
                    className={classes.input}
                    value={amounts[index]}
                    onChange={(e) => handleAmountChange(index, e.target.value)}
                  />
                </td>
                <td className={classes.td}>
                  <Typography sx={{ minWidth: 80 }}>
                    ${total.toLocaleString()}
                  </Typography>
                </td>
              </tr>
            );
          })}

          <tr className={classes.tableFooter}>
            <td>Total order:</td>
            <td></td>
            <td></td>
            <td className={classes.tableFooterItem}>
              <Button
                className={classes.buttonBuy}
                onClick={() => handleBuy()}
                disabled={disabled}
              >
                Buy
              </Button>
              <Button
                className={classes.buttonSell}
                onClick={() => handleSell()}
                disabled={disabled}
              >
                Sell
              </Button>
            </td>

            <td style={{ padding: 16 }}>${toUSDFormat(totalPrice)}</td>
          </tr>
        </tbody>
      </table>
    </Box>
  );
};

export default NarcoticsList;
